import React, { useEffect, useState, useCallback } from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ProductsTable from './components/ProductsTable';
import ProductPortfolioDashboard from './components/ProductPortfolioDashboard';
import TaxonomyQualityWidget from './components/TaxonomyQualityWidget';
import PageContainer from './components/common/PageContainer';
import CollapsibleSection from './components/common/CollapsibleSection';
import ViewHeader from './components/ViewHeader';
import { SP } from './theme/tokens';
import useAdminSync from './hooks/useAdminSync';

export default function ProductsOverview() {
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const { runFullProductSync, fullSync, runMarketSync } = useAdminSync();

  // Label tracks the orchestrated 2-step sequence:
  //   idle → "Sync products"
  //   step 1 (products) → "Syncing products…"
  //   step 2 (lsm-sync) → "Syncing X / N" once counts arrive, else
  //                       "LSM Control sync…" (matches the header panel label)
  //   error → "Sync failed — retry" for ~5s (button stays clickable), then idle
  let syncLabel = 'Sync products';
  if (fullSync.error) {
    syncLabel = 'Sync failed — retry';
  } else if (fullSync.running) {
    if (fullSync.step === 'lsm-sync') {
      syncLabel = fullSync.total ? `Syncing ${fullSync.done} / ${fullSync.total}` : 'LSM Control sync…';
    } else {
      syncLabel = 'Syncing products…';
    }
  }

  const handleGlobalRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);
  useEffect(() => {
    window.addEventListener('price-updated', handleGlobalRefresh);
    window.addEventListener('pricing-strategy-changed', handleGlobalRefresh);
    window.addEventListener('admin-sync:products-done', handleGlobalRefresh);
    return () => {
      window.removeEventListener('price-updated', handleGlobalRefresh);
      window.removeEventListener('pricing-strategy-changed', handleGlobalRefresh);
      window.removeEventListener('admin-sync:products-done', handleGlobalRefresh);
    };
  }, [handleGlobalRefresh]);

  return (
    /* 🔴 G1b (§2.6): `/products` trägt das Breitenmuster **`full`** (WF3 der
       Routentabelle `inhaltsbreite.md` §2.2). Sichtbar wird das erst ab ≈1920
       (darunter deckelt `reading` ohnehin bei 1280); ab dort fällt der Deckel,
       die Tabelle bekommt mehr Raum, und `TableViewport` meldet `SEARCH_MAX`
       an die Suchpille (NW4.2, PW10). */
    <PageContainer width="full">
      {/* 🔴 `liste-kopf` — die fünfte §15-Zonenmarke, von Hand gesetzt
          (`concepts/tabellen-filter-suche.md` §15, G2-Grill B2: KEIN Baustein
          stempelt sie). Sie sitzt um den `ViewHeader`, wie in der Musterroute
          (`ListenMuster.jsx:483-495`). Bis G1b fehlte sie auf `/products`;
          `oeffne()` wartet auf `liste-panel`, nicht auf diese Marke — die
          Proben P3/P20/P32/P33 lesen sie aber. */}
      <Box data-testid="liste-kopf">
      <ViewHeader
        title="Products Overview"
        breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Products' }]}
        /* 🔴 E6 (UI-Durchgang 2, 2026-10-07): EIN Primärknopf statt vier
           Einzelknöpfe in einer Ecke (outlined „Second-Life", contained
           „Sync Products", outlined „Sync Market Prices", text „Attribute
           editor" — vier Stile, keine Rangfolge). Primär ist „Sync products"
           mit dem Fortschritt aus `fullSync`; „Sync market prices" ist die
           Variante derselben Handlung an derselben Sache.
           Der zweite Life-Eingang wandert in den ⋯-Überlauf.
           🔴 KM-1 (2026-10-10): **„Sync market prices" sitzt im ⋯, NICHT mehr
           im ▾.** Diese Seite hat einen ⋯ (Second-Life-Eingänge, Attribute
           editor), und ein Seitenkopf zeigt höchstens EIN Menü. Der Kopf
           rendert „Sync products" deshalb als einfachen `ProgressButton` (der
           Fortschritt bleibt, er kommt aus `fullSync`) und stellt „Sync market
           prices" als obersten ⋯-Eintrag darüber — abgesetzt vom Rest durch
           einen Trenner. Die Begründung von E6 bleibt inhaltlich: es ist
           dieselbe Handlung an derselben Sache, also steht sie oben und nicht
           unter den Wegen zu anderen Seiten.
           🔴 W14 (2026-10-07): das `emphasis: 'primary'` unten ist PFLICHT und
           stand schon dort. Ohne es faellt die Aktion auf den Rueckfall
           („die erste Aktion") — hier zufaellig dieselbe, aber sobald eine
           zweite Aktion davorsteht, waere die Primaerhandlung nicht mehr
           gefuellt. Die Probe dazu: `ViewHeader.kopfaktionen.test.jsx` (13). */
        actions={[
          {
            key: 'sync-products',
            label: syncLabel,
            emphasis: 'primary',
            onClick: runFullProductSync,
            running: fullSync.running,
            progress: fullSync.progress,
            menu: [
              { key: 'sync-market', label: 'Sync market prices', onClick: runMarketSync },
            ],
          },
        ]}
        /* 🔴 Der Eingang zur Second-Life-Ansicht — gemessen, nicht abgewogen.
           `docs/reviews/2026-09-23-second-life-market.ux-review.md:343` fordert
           seine EXISTENZ: ohne ihn ist die Seite nur ueber eine eingetippte URL
           erreichbar. Das ⋯ erfuellt das — erreichbar, aber nicht dauerhaft im
           Kopf. Dieselbe Bauform wie die Geschwister-Warteschlangen in
           `components/ScraperList.jsx` (Preis-Pruefer, Redirect-Reviews);
           keiner von beiden hat einen Sidebar-Eintrag.
           🔴 **Seit dem 2026-10-10 EIN Eintrag, nicht zwei.** `/second-life/bestand`
           ist entfallen, `/second-life` IST die Bestandsansicht (Karussell oben,
           Tabelle unten). Zwei Eintraege mit demselben Ziel waeren zwei
           Bedienelemente fuer eine Handlung.
           🔴 OFFEN und NICHT Teil dieses Schritts:
           `docs/reviews/2026-09-27-second-life-umbau.ux-review.md:96-104`
           fordert zusaetzlich ein SIGNAL, dass Arbeit wartet (Zaehler). Das
           liefert `/api/second-life/bestand` nicht — eigener Befund.
           (`/api/second-life/queue` nannte diese Zeile bis zum 2026-10-10; die
           Route ist entfallen.) */
        overflow={[
          // 🔴 R16 N-3 (2026-10-07): „Second-Life queue" → „Second-Life". Die drei
          // Warteschlangen-Eingaenge heissen jetzt gleich: „Price Checker",
          // „Redirect Reviews (n)", „Second Life" — Sache, nicht Sache + „queue".
          // 🔴 R16-3 (2026-10-10): der Bindestrich ist gefallen.
          // 🔴 **Und der ZWEITE Eintrag ist weg** (2026-10-10, „eine Ansicht"):
          // `/second-life/bestand` gibt es nicht mehr — `/second-life` IST die
          // Bestandsansicht. Ein ⋯-Eintrag dorthin zeigte auf die eigene Seite.
          // Strang 1a-6: der Eingang zur Vollstaendigkeits-Uebersicht. Er steht
          // HIER und nicht in der Seitenleiste: die Ansicht ist eine Sicht auf
          // DIESE Liste („welche Zeile hat Luecken?"), kein eigener
          // Arbeitsbereich — dieselbe Bauform wie die Warteschlangen der
          // Geschwisterliste (`components/ScraperList.jsx`). Der Weg von einer
          // Zeile aus ist die Sektion am Produkt selbst.
          { key: 'completion', label: 'Completeness', onClick: () => navigate('/products/completion') },
          { key: 'second-life', label: 'Second Life', onClick: () => navigate('/second-life') },
          { key: 'attribute-editor', label: 'Attribute editor', onClick: () => navigate('/products/attributes') },
        ]}
      />
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* 🔴 §4.1 — die zwei Dashboard-Widgets verlassen den ersten Bildschirm.
            Gemessen (Konzept §1.1): die Seite hatte 1905 DOM-Knoten, und die
            Tabelle begann erst unter y ≈ 900 — der Bediener sah zwei
            Analysflaechen, bevor er das erste Produkt sah, obwohl die Seite
            „Products" heisst.

            Die erste Wahl des Konzepts war „nach Home verschieben". Home ist
            hier NICHT Teil des Postens (W-33, bereits abgeschlossen), also die
            zweite, ausdruecklich zugelassene Wahl: EINE eingeklappte
            `CollapsibleSection` statt zwei offener Widgets. Nichts ist
            versteckt, es ist einen Klick entfernt.

            ⚠️ Was das NICHT behebt: den ZWEITEN Abruf von
            `/api/product_overview` (241 Zeilen, §1.2). MUI `Collapse` laesst
            seine Kinder montiert, `CollapsibleSection` hat keinen
            `unmountOnExit`-Schalter — der Abruf passiert also weiter beim
            ersten Rendern. Das saubere Ende ist der Umzug nach Home oder ein
            `unmountOnExit`-Prop am geteilten Bauteil; beides liegt ausserhalb
            dieses Postens und ist im Bericht vermerkt. */}
        <CollapsibleSection
          title="Portfolio"
          summary="Sales, stock and taxonomy quality"
          defaultExpanded={false}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: SP.card }}>
            <ProductPortfolioDashboard />
            <TaxonomyQualityWidget />
          </Box>
        </CollapsibleSection>
        <ProductsTable key={refreshKey} />
      </Box>
    </PageContainer>
  );
}
